import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Register.css';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    updateLocalStorageFromQueryParams,
    buildQueryParamsFromStorage
} from '../../Helper/storageUtils';
import Billede1 from '../../Images/Billede1.jpg';
import HeroSectionV2 from '../../components/HeroSectionV2';

interface Country {
    code: string;
}

const Register: React.FC = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        updateLocalStorageFromQueryParams(params, ['eventId', 'club', 'media', 'team']);
    }, [location]);

    const buildLoginQueryParams = () => {
        const params = new URLSearchParams();
        ['eventId', 'club', 'media', 'team'].forEach(key => {
            const val = localStorage.getItem(key);
            if (val) params.set(key.toLowerCase(), key === 'eventId' ? val : 'true');
        });
        return params.toString();
    };

    // -------------------- ADD: Dynamic countries --------------------
    const [countries, setCountries] = useState<Country[]>([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/countries`);
                setCountries(response.data); // assume backend returns [{code: 'DEN'}, {code: 'USA'}, ...]
            } catch (error) {
                console.error('Failed to fetch countries', error);
            }
        };
        fetchCountries();
    }, []);
    // -----------------------------------------------------------------

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        dateOfBirth: '',
        countryCode: '', // match backend
        profilePicture: ''
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRegister = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (formData.password !== formData.confirmPassword) {
            setErrorMessage(language === 'en' ? 'Passwords do not match.' : 'Kodeordene er ikke ens.');
            return;
        }

        const age = getAge(formData.dateOfBirth);
        if (age < 12) {
            setErrorMessage(language === 'en'
                ? 'You must be at least 12 years old to register.'
                : 'Du skal være mindst 12 år for at oprette en konto.');
            return;
        }

        try {
            const payload = {
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                dateOfBirth: formData.dateOfBirth,
                country: formData.countryCode,      // <-- must match DTO property name
                personImage: formData.profilePicture // <-- must match DTO property name
            };


            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users/register`, payload);

            if (response.status === 201) {
                setSuccessMessage(language === 'en'
                    ? 'Account created successfully! Redirecting to login...'
                    : 'Kontoen er oprettet! Omdirigerer til login...');

                setTimeout(() => {
                    const params = buildLoginQueryParams();
                    navigate(params ? `/login?${params}` : '/login');
                }, 2500);
            }
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || (language === 'en'
                ? 'An error occurred during registration.'
                : 'Der opstod en fejl under registreringen.'));
        }
    };

    const getAge = (dob: string) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };


    return (
        <div className="home">
            <HeroSectionV2
                    titleEn="Welcome to Badminton Denmark"
                    titleDa="Velkommen til Badminton Danmark"
                    descriptionEn="Login or sign up to access all the features!"
                    descriptionDa="Log ind eller opret en profil for at få adgang til alle funktioner!"
                    imageSrc={Billede1}
                />
            <div className="register">
                <h1>{language === 'en' ? 'Create an Account' : 'Opret Profil'}</h1>

                <form onSubmit={handleRegister} className="register-form">
                    <div className="form-row">
                        <div className="form-group full-width">
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Email' : 'E-mail'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="password" name="password" value={formData.password} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Password' : 'Kodeord'}</label>
                        </div>
                        <div className="form-group">
                            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Confirm Password' : 'Bekræft Kodeord'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                            <label>{language === 'en' ? 'First Name' : 'Fornavn'}</label>
                        </div>

                        <div className="form-group">
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} />
                            <label>{language === 'en' ? 'Last Name' : 'Efternavn'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                            <label>{language === 'en' ? 'Phone' : 'Telefon'}</label>
                        </div>

                        <div className="form-group">
                            <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required max={new Date().toISOString().split('T')[0]} />
                            <label>{language === 'en' ? 'Date of Birth' : 'Fødselsdato'}</label>
                        </div>
                    </div>

                    {/* ✅ COUNTRY DROPDOWN */}
                    <div className="form-row">
                        <div className="form-group full-width">
                            <select name="countryCode" value={formData.countryCode} onChange={handleChange} required>
                                <option value="">
                                    {language === 'en' ? 'Select country' : 'Vælg land'}
                                </option>
                                {countries.map((c: Country) => (
                                    <option key={c.code} value={c.code}>{c.code}</option>
                                ))}
                            </select>
                            <label>{language === 'en' ? 'Country' : 'Land'}</label>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <input type="file" accept="image/*" onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    const reader = new FileReader();
                                    reader.onload = () => setFormData({ ...formData, profilePicture: reader.result as string });
                                    reader.readAsDataURL(e.target.files[0]);
                                }
                            }} />
                            <label>{language === 'en' ? 'Profile Picture (optional)' : 'Profilbillede (valgfrit)'}</label>
                        </div>
                    </div>

                    <button type="submit" className="register-button">
                        {language === 'en' ? 'Sign Up' : 'Opret Profil'}
                    </button>

                    {errorMessage && <p className="error">{errorMessage}</p>}
                    {successMessage && <p className="success">{successMessage}</p>}
                </form>
            </div>
        </div>
    );
};

export default Register;